import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAlert } from '../contexts/AlertContext';
import './RankingsPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const RankingsPage = () => {
  const { showError } = useAlert();
  const [rankings, setRankings] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('total_score');
  const [activePeriod, setActivePeriod] = useState('all');

  const rankingTypes = {
    total_score: { 
      name: '총 점수 랭킹', 
      icon: '',
      description: '누적 점수 기준'
    },
    accuracy: { 
      name: '정답률 랭킹', 
      icon: '',
      description: '정답률 기준'
    },
    most_active: { 
      name: '최다 플레이 랭킹', 
      icon: '',
      description: '게임 횟수 기준'
    }
  };

  const periods = {
    all: '전체',
    daily: '일간',
    weekly: '주간', 
    monthly: '월간'
  };

  useEffect(() => {
    fetchSummary();
    fetchRankings();
  }, [activeTab, activePeriod]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/leaderboard/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Summary fetch error:', error);
      showError('리더보드 요약 정보를 불러오는데 실패했습니다.');
    }
  };

  const fetchRankings = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (activeTab) {
        case 'total_score':
          endpoint = `${API_BASE_URL}/api/rankings/total-score?period=${activePeriod}&limit=50`;
          break;
        case 'accuracy':
          endpoint = `${API_BASE_URL}/api/rankings/accuracy?period=${activePeriod}&limit=50`;
          break;
        case 'most_active':
          endpoint = `${API_BASE_URL}/api/rankings/most-active?period=${activePeriod}&limit=50`;
          break;
        default:
          endpoint = `${API_BASE_URL}/api/rankings/total-score?period=${activePeriod}&limit=50`;
      }

      const response = await axios.get(endpoint);
      setRankings(response.data.rankings || []);
    } catch (error) {
      console.error('Rankings fetch error:', error);
      showError('랭킹 정보를 불러오는데 실패했습니다.');
      setRankings([]);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `${rank}위`;
    }
  };

  const formatScore = (score) => {
    return new Intl.NumberFormat('ko-KR').format(Math.round(score));
  };

  const renderRankingItem = (item, index) => {
    const isTopThree = item.rank <= 3;
    
    return (
      <div key={index} className={`ranking-item ${isTopThree ? 'top-three' : ''}`}>
        <div className="rank-info">
          <div className="rank-number">{getRankIcon(item.rank)}</div>
          <div className="user-info">
            <div className="user-name">{item.name}</div>
            <div className="user-grade">{item.grade}</div>
          </div>
        </div>
        
        <div className="stats-info">
          {activeTab === 'total_score' && (
            <>
              <div className="primary-stat">
                <span className="stat-value">{formatScore(item.total_score)}</span>
                <span className="stat-label">총점</span>
              </div>
              <div className="secondary-stats">
                <div className="stat-item">
                  <span className="stat-value">{item.total_games}</span>
                  <span className="stat-label">게임</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{formatScore(item.average_score)}</span>
                  <span className="stat-label">평균</span>
                </div>
              </div>
            </>
          )}
          
          {activeTab === 'accuracy' && (
            <>
              <div className="primary-stat">
                <span className="stat-value">{item.accuracy_rate}%</span>
                <span className="stat-label">정답률</span>
              </div>
              <div className="secondary-stats">
                <div className="stat-item">
                  <span className="stat-value">{item.correct_answers}</span>
                  <span className="stat-label">정답</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{item.total_answers}</span>
                  <span className="stat-label">총답변</span>
                </div>
              </div>
            </>
          )}
          
          {activeTab === 'most_active' && (
            <>
              <div className="primary-stat">
                <span className="stat-value">{item.total_games}</span>
                <span className="stat-label">게임</span>
              </div>
              <div className="secondary-stats">
                <div className="stat-item">
                  <span className="stat-value">{formatScore(item.total_score)}</span>
                  <span className="stat-label">총점</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{formatScore(item.average_score)}</span>
                  <span className="stat-label">평균</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="rankings-page">
      <div className="rankings-header">
        <h1>Rankings</h1>
        <p>사용자들의 점수와 성과를 확인해보세요</p>
      </div>

      {/* 요약 통계 */}
      <div className="summary-stats">
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-number">{summary.total_users || 0}</div>
            <div className="stat-label">전체 사용자</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-number">{summary.total_completed_games || 0}</div>
            <div className="stat-label">완료된 게임</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-number">{formatScore(summary.highest_score || 0)}</div>
            <div className="stat-label">최고 점수</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-number">{summary.today_active_users || 0}</div>
            <div className="stat-label">오늘 활성 사용자</div>
          </div>
        </div>
      </div>

      {/* 랭킹 타입 탭 */}
      <div className="ranking-tabs">
        {Object.entries(rankingTypes).map(([key, type]) => (
          <button
            key={key}
            className={`ranking-tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            <span className="tab-icon">{type.icon}</span>
            <div className="tab-content">
              <div className="tab-name">{type.name}</div>
              <div className="tab-description">{type.description}</div>
            </div>
          </button>
        ))}
      </div>

      {/* 기간 필터 */}
      <div className="period-filter">
        {Object.entries(periods).map(([key, label]) => (
          <button
            key={key}
            className={`period-btn ${activePeriod === key ? 'active' : ''}`}
            onClick={() => setActivePeriod(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 랭킹 리스트 */}
      <div className="rankings-container">
        <div className="ranking-header">
          <h2>
            {rankingTypes[activeTab].icon} {rankingTypes[activeTab].name}
            <span className="period-label">({periods[activePeriod]})</span>
          </h2>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner">⭐</div>
            <p>랭킹을 불러오는 중...</p>
          </div>
        ) : rankings.length === 0 ? (
          <div className="empty-rankings">
            <div className="empty-icon">📊</div>
            <h3>랭킹 데이터가 없습니다</h3>
            <p>아직 {periods[activePeriod]} 기간의 랭킹 정보가 없습니다.</p>
          </div>
        ) : (
          <div className="rankings-list">
            {rankings.map((item, index) => renderRankingItem(item, index))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RankingsPage; 